import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { contractApi, ContractPageResponse, UpdateOrganizationInfoRequest } from '@/api/contract'
import { Pencil, X, Save } from 'lucide-react'
import { toast } from 'sonner'

function InfoRow({ label, value, field, type = 'text', isEditing, formValue, onChange }: {
  label: string
  value: string
  field?: string
  type?: string
  isEditing: boolean
  formValue?: string
  onChange?: (value: string) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100 last:border-0">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="col-span-2 text-sm text-gray-900">
        {isEditing && field ? (
          <input
            type={type}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={formValue || ''}
            onChange={(e) => onChange?.(e.target.value)}
          />
        ) : (
          value || '-'
        )}
      </dd>
    </div>
  )
}

interface Props {
  data: ContractPageResponse
}

export default function CustomerInfoSection({ data }: Props) {
  const { t } = useTranslation('contract')
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState<UpdateOrganizationInfoRequest>({})

  const updateMutation = useMutation({
    mutationFn: (req: UpdateOrganizationInfoRequest) => contractApi.updateOrganizationInfo(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract'] })
      setIsEditing(false)
      toast.success(t('customer.updateSuccess'))
    },
    onError: () => {
      toast.error(t('customer.updateError'))
    },
  })

  const startEdit = () => {
    setForm({
      name: data.name,
      businessRegistrationNumber: data.businessRegistrationNumber || undefined,
      representativeName: data.representativeName || undefined,
      businessCategory: data.businessCategory || undefined,
      address: data.address || undefined,
      phoneNumber: data.phoneNumber || undefined,
      email: data.email || undefined,
    })
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setForm({})
  }

  const handleSave = () => {
    updateMutation.mutate(form)
  }

  const orgTypeLabel = data.organizationType === 'company' ? t('customer.orgTypeCompany') : data.organizationType === 'learning_center' ? t('customer.orgTypeLearningCenter') : data.organizationType

  const addressText = data.address
    ? [data.address.state, data.address.city, data.address.addressLine1, data.address.addressLine2].filter(Boolean).join(' ')
    : '-'

  const handleFieldChange = useCallback((field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }, [])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{t('customer.title')}</h3>
        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={cancelEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X className="w-4 h-4" />
              {t('customer.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {t('customer.save')}
            </button>
          </div>
        ) : (
          <button
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Pencil className="w-4 h-4" />
            {t('customer.edit')}
          </button>
        )}
      </div>
      <dl className="px-6 py-2">
        <InfoRow label={t('customer.orgType')} value={orgTypeLabel} isEditing={false} />
        <InfoRow label={t('customer.name')} value={data.name} field="name" isEditing={isEditing} formValue={form.name} onChange={(v) => handleFieldChange('name', v)} />
        <InfoRow label={t('customer.businessRegNumber')} value={data.businessRegistrationNumber || '-'} field="businessRegistrationNumber" isEditing={isEditing} formValue={form.businessRegistrationNumber} onChange={(v) => handleFieldChange('businessRegistrationNumber', v)} />
        <InfoRow label={t('customer.representativeName')} value={data.representativeName || '-'} field="representativeName" isEditing={isEditing} formValue={form.representativeName} onChange={(v) => handleFieldChange('representativeName', v)} />
        <InfoRow label={t('customer.businessCategory')} value={data.businessCategory || '-'} field="businessCategory" isEditing={isEditing} formValue={form.businessCategory} onChange={(v) => handleFieldChange('businessCategory', v)} />
        <div className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100">
          <dt className="text-sm font-medium text-gray-500">{t('customer.address')}</dt>
          <dd className="col-span-2 text-sm text-gray-900">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  placeholder={t('customer.addressState')}
                  value={form.address?.state || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, address: { ...prev.address, state: e.target.value, addressLine1: prev.address?.addressLine1 || null, addressLine2: prev.address?.addressLine2 || null, city: prev.address?.city || null, postalCode: prev.address?.postalCode || null } }))}
                />
                <input
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  placeholder={t('customer.addressCity')}
                  value={form.address?.city || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, address: { ...prev.address, city: e.target.value, addressLine1: prev.address?.addressLine1 || null, addressLine2: prev.address?.addressLine2 || null, state: prev.address?.state || null, postalCode: prev.address?.postalCode || null } }))}
                />
                <input
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  placeholder={t('customer.addressDetail')}
                  value={form.address?.addressLine1 || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, address: { ...prev.address, addressLine1: e.target.value, addressLine2: prev.address?.addressLine2 || null, city: prev.address?.city || null, state: prev.address?.state || null, postalCode: prev.address?.postalCode || null } }))}
                />
                <input
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  placeholder={t('customer.postalCode')}
                  value={form.address?.postalCode || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, address: { ...prev.address, postalCode: e.target.value, addressLine1: prev.address?.addressLine1 || null, addressLine2: prev.address?.addressLine2 || null, city: prev.address?.city || null, state: prev.address?.state || null } }))}
                />
              </div>
            ) : (
              addressText
            )}
          </dd>
        </div>
        <InfoRow label={t('customer.phone')} value={data.phoneNumber || '-'} field="phoneNumber" isEditing={isEditing} formValue={form.phoneNumber} onChange={(v) => handleFieldChange('phoneNumber', v)} />
        <InfoRow label={t('customer.email')} value={data.email || '-'} field="email" type="email" isEditing={isEditing} formValue={form.email} onChange={(v) => handleFieldChange('email', v)} />
      </dl>
    </div>
  )
}
